<script>
/**
 * @component PatientVideoRoom
 * @description Landing pública de videoconsulta del paciente. Pantalla blanca
 * de preparación: datos del médico, adjuntar documentos para que el médico
 * revise, token de validación, y entrar a la sala a esperar al médico.
 * Reemplaza la antigua pantalla negra de "Sala no disponible".
 * Accedida via URL enviada por el médico: /appointment/:appointmentId/:patientId
 * No requiere autenticación — usa patientId de la URL como identidad.
 */
import Twilio from "@/components/video-room/Twilio.vue";
import appointmentService from "@/services/appointment";
import videoRoomService from "@/services/videoRoom";

/** @description Intervalo de polling para detectar que el médico se conectó */
const POLL_MS = 8000;

export default {
  name: "PatientVideoRoom",

  components: { Twilio },

  data() {
    return {
      /** @type {string} loading | ready | not-found */
      loadState: "loading",
      doctor: null,
      medicalSpecialty: null,
      startsAt: null,
      attachments: [],
      room: null,
      /** @type {string} Token de validación — visible, sin wiring por ahora */
      token: "",
      /** @type {boolean} El paciente tocó "Entrar a la consulta" */
      entered: false,
      /** @type {string|null} Access token de Twilio */
      accessToken: null,
      isUploading: false,
      isConnecting: false,
      pollTimer: null,
    };
  },

  computed: {
    /** @returns {string} ID del turno desde la URL */
    appointmentId() {
      return this.$route.params.appointmentId;
    },
    /** @returns {string} ID del paciente desde la URL */
    patientId() {
      return this.$route.params.patientId;
    },
    /** @returns {string} Nombre del médico con prefijo Dr. */
    doctorName() {
      if (!this.doctor) return "el médico";
      const full = `${this.doctor.firstName || ""} ${
        this.doctor.lastName || ""
      }`.trim();
      return full ? `Dr. ${full}` : "el médico";
    },
    /** @returns {string} Iniciales del médico para el avatar */
    doctorInitials() {
      if (!this.doctor) return "";
      const f = (this.doctor.firstName || "")[0] || "";
      const l = (this.doctor.lastName || "")[0] || "";
      return (f + l).toUpperCase();
    },
    /** @returns {string} Especialidad + horario para el subtítulo */
    headerMeta() {
      const parts = [];
      if (this.medicalSpecialty && this.medicalSpecialty.name) {
        parts.push(this.medicalSpecialty.name);
      }
      if (this.startsAt) {
        const d = new Date(this.startsAt);
        const time = d.toLocaleTimeString("es-AR", {
          hour: "2-digit",
          minute: "2-digit",
        });
        const isToday = d.toDateString() === new Date().toDateString();
        const day = isToday
          ? "Hoy"
          : d.toLocaleDateString("es-AR", {
              day: "2-digit",
              month: "2-digit",
            });
        parts.push(`${day} ${time}`);
      }
      return parts.join(" · ");
    },
    /** @returns {boolean} El médico está conectado (hay sala activa) */
    doctorOnline() {
      return !!(this.room && this.room._id);
    },
    /** @returns {boolean} Mostrar la videollamada en vivo */
    inCall() {
      return this.entered && this.doctorOnline && !!this.accessToken;
    },
    /** @returns {string} Título de la pantalla de espera según el estado */
    waitTitle() {
      if (this.entered && !this.doctorOnline)
        return "Estás en la sala de espera";
      if (this.doctorOnline) return "El médico ya está en la sala";
      return "Tu videoconsulta está lista";
    },
    /** @returns {string} Subtítulo de la pantalla de espera según el estado */
    waitSub() {
      if (this.entered && !this.doctorOnline) {
        return "El médico todavía no ingresó. Quedate en esta pantalla — apenas entre, te conectamos automáticamente.";
      }
      if (this.entered && this.doctorOnline) {
        return "Conectándote a la videollamada...";
      }
      if (this.doctorOnline) {
        return 'Tocá "Entrar a la consulta" para unirte ahora.';
      }
      return 'Tocá "Entrar a la consulta" para esperar al médico. Apenas entres, va a ver que llegaste.';
    },
  },

  async created() {
    await this.loadLanding();
    this.startPolling();
  },

  beforeDestroy() {
    this.stopPolling();
  },

  methods: {
    /**
     * @description Carga los datos de la landing (médico, adjuntos, sala).
     */
    async loadLanding() {
      try {
        const data = await appointmentService.getVideoConsultationLanding(
          this.appointmentId,
          this.patientId
        );
        this.doctor = data.doctor;
        this.medicalSpecialty = data.medicalSpecialty;
        this.startsAt = data.startsAt;
        this.attachments = data.attachments || [];
        this.room = data.room;
        this.loadState = "ready";
        // Si el paciente ya entró y recién apareció la sala → conectar.
        if (this.entered && this.doctorOnline && !this.accessToken) {
          this.connect();
        }
      } catch {
        if (this.loadState === "loading") this.loadState = "not-found";
      }
    },

    /** @description Arranca el polling para detectar al médico. */
    startPolling() {
      this.pollTimer = setInterval(() => {
        // Heartbeat de presencia: solo mientras el paciente entró a la sala.
        // Si todavía está en el link sin tocar "Entrar", no se marca presencia.
        if (this.entered) this.markPresence();
        if (!this.inCall) this.loadLanding();
      }, POLL_MS);
    },

    /** @description Frena el polling. */
    stopPolling() {
      if (this.pollTimer) clearInterval(this.pollTimer);
      this.pollTimer = null;
    },

    /**
     * @description El paciente entra a la sala. Si el médico ya está, conecta;
     * si no, queda esperando y el polling conecta cuando aparezca la sala.
     */
    async enterConsultation() {
      this.entered = true;
      // Marcar presencia ya — no esperar al próximo ciclo del poll.
      this.markPresence();
      if (this.doctorOnline) await this.connect();
    },

    /**
     * @description El paciente sale de la sala de espera. Frena el heartbeat
     * de presencia: tiene el mismo efecto que cerrar la pestaña — el indicador
     * "En sala" del médico se apaga solo a los ~30s.
     */
    cancelConsultation() {
      this.entered = false;
      this.accessToken = null;
    },

    /**
     * @description Envía el heartbeat de presencia al backend: le avisa que el
     * paciente está conectado a la sala de espera, para que el médico lo vea
     * "En sala" en Atenciones. Best-effort — si falla, el próximo ciclo
     * reintenta.
     */
    async markPresence() {
      try {
        await appointmentService.markVideoPresence(
          this.appointmentId,
          this.patientId
        );
      } catch {
        // best-effort
      }
    },

    /** @description Obtiene el token de Twilio y entra a la videollamada. */
    async connect() {
      if (this.accessToken || this.isConnecting || !this.room) return;
      this.isConnecting = true;
      try {
        this.accessToken = await videoRoomService.getPatientRoomAccessToken(
          this.room._id,
          this.patientId
        );
      } catch {
        // Si falla, el polling reintenta en el próximo ciclo.
      }
      this.isConnecting = false;
    },

    /** @description Abre el selector de archivos. */
    triggerFilePicker() {
      this.$refs.fileInput.click();
    },

    /**
     * @description Sube el archivo elegido como adjunto de la consulta.
     * @param {Event} e - change del input file
     */
    async onFileSelected(e) {
      const file = e.target.files && e.target.files[0];
      e.target.value = "";
      if (!file) return;
      this.isUploading = true;
      try {
        const { attachments } =
          await appointmentService.uploadAppointmentAttachment(
            this.appointmentId,
            this.patientId,
            file
          );
        this.attachments = attachments || [];
      } catch {
        if (this.$message) {
          this.$message.error("No se pudo subir el documento.");
        }
      }
      this.isUploading = false;
    },

    /**
     * @description Quita un adjunto de la consulta.
     * @param {string} attachmentId - ID del adjunto
     */
    async removeAttachment(attachmentId) {
      try {
        const { attachments } =
          await appointmentService.deleteAppointmentAttachment(
            this.appointmentId,
            attachmentId,
            this.patientId
          );
        this.attachments = attachments || [];
      } catch {
        if (this.$message) {
          this.$message.error("No se pudo quitar el documento.");
        }
      }
    },

    /**
     * @description Formatea el tamaño de un archivo.
     * @param {number} bytes
     * @returns {string}
     */
    formatSize(bytes) {
      if (!bytes) return "";
      const mb = bytes / (1024 * 1024);
      if (mb >= 1) return `${mb.toFixed(1)} MB`;
      return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    },
  },
};
</script>

<template lang="pug">
.pvr
  header.pvr-top
    .pvr-logo
      span Quantux&nbsp;
      b Salud

  //- Cargando
  .pvr-center(v-if="loadState === 'loading'")
    .pvr-spinner
    p Cargando tu videoconsulta...

  //- Enlace inválido
  .pvr-center(v-else-if="loadState === 'not-found'")
    .pvr-nf-icon
      i.el-icon-warning-outline
    h2 No encontramos la consulta
    p El enlace no es válido o el turno ya no está disponible.

  //- Landing
  main.pvr-layout(v-else)
    //- Stage: video / sala de espera
    section.pvr-stage(:class="{ 'pvr-stage--call': inCall }")
      Twilio(
        v-if="inCall"
        :patient-id="patientId"
        :appointment-id="appointmentId"
        :identity="patientId"
        :external-token="accessToken"
        :external-room-id="room._id"
        :autoJoin="true"
      )
      .pvr-wait(v-else)
        .pvr-wait-badge
          i.el-icon-time
        p.pvr-wait-title {{ waitTitle }}
        p.pvr-wait-sub {{ waitSub }}
        span.pvr-pulse(v-if="entered && !doctorOnline")
          span.pvr-dot
          | Esperando al médico

    //- Sidebar: preparación
    aside.pvr-side
      .pvr-appt
        .pvr-avatar {{ doctorInitials }}
        .pvr-appt-info
          span.pvr-kind Videoconsulta
          h1.pvr-doctor {{ doctorName }}
          span.pvr-meta {{ headerMeta }}

      section.pvr-block
        .pvr-block-head
          i.el-icon-paperclip
          h2 Documentos para la consulta
        p.pvr-block-desc Subí estudios, recetas o cualquier información que quieras que el médico revise.
        button.pvr-attach(@click="triggerFilePicker" :disabled="isUploading")
          | {{ isUploading ? "Subiendo..." : "+ Adjuntar documento" }}
        input.pvr-file-input(
          ref="fileInput"
          type="file"
          @change="onFileSelected"
        )
        .pvr-file(v-for="f in attachments" :key="f._id")
          .pvr-file-ic
            i.el-icon-document
          .pvr-file-meta
            span.pvr-file-name {{ f.name }}
            span.pvr-file-size {{ formatSize(f.size) }}
          span.pvr-file-x(@click="removeAttachment(f._id)")
            i.el-icon-close

      section.pvr-block
        .pvr-block-head
          h2 Token de validación
          span.pvr-opt si tu consulta lo requiere
        input.pvr-token(
          v-model="token"
          type="text"
          placeholder="Ingresá el código"
        )

      .pvr-cta-wrap(v-if="!inCall")
        button.pvr-cta(
          v-if="!entered"
          @click="enterConsultation"
        )
          i.el-icon-video-camera
          span Entrar a la consulta
        button.pvr-cta.pvr-cta--cancel(
          v-else
          @click="cancelConsultation"
        )
          i.el-icon-close
          span Cancelar
        p.pvr-cta-hint(v-if="!entered") Entrá ahora — el médico va a ver que lo estás esperando.
        p.pvr-cta-hint(v-else) Estás en la sala de espera — el médico ya ve que llegaste.
</template>

<style lang="scss" scoped>
$navy: #002d5e;
$teal: #05e5ba;
$bg: #eef2f7;
$text: #16243b;
$muted: #6e7c90;
$line: #e7ecf2;

.pvr {
  min-height: 100vh;
  background: $bg;
  font-family: "Plus Jakarta Sans", sans-serif;
  color: $text;
}

.pvr-top {
  background: #fff;
  border-bottom: 1px solid $line;
  padding: 14px 24px;
  display: flex;
  align-items: center;
}
.pvr-logo {
  font-size: 16px;
  color: $navy;
  b {
    font-weight: 800;
  }
  span {
    font-weight: 700;
  }
}

/* ---- Estados loading / not-found ---- */
.pvr-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 80px 24px;
  color: $muted;

  h2 {
    color: $navy;
    font-size: 20px;
    margin: 16px 0 8px;
  }
  p {
    font-size: 14px;
  }
}
.pvr-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #dde4ec;
  border-top-color: $teal;
  border-radius: 50%;
  animation: pvr-spin 0.8s linear infinite;
  margin-bottom: 16px;
}
@keyframes pvr-spin {
  to {
    transform: rotate(360deg);
  }
}
.pvr-nf-icon {
  font-size: 46px;
  color: #e0a92e;
}

/* ---- Layout 2 columnas (responsive) ---- */
.pvr-layout {
  max-width: 1140px;
  margin: 0 auto;
  padding: 24px 16px 40px;
  display: flex;
  gap: 20px;
  align-items: stretch;
}

/* ---- Stage ---- */
.pvr-stage {
  flex: 1.7;
  background: #f4f7fb;
  border: 1px solid $line;
  border-radius: 18px;
  min-height: 480px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px;

  &--call {
    padding: 0;
    overflow: hidden;
    background: #000;
  }
}
::v-deep .pvr-stage--call .video-chat {
  height: 100%;
  min-height: 480px;
}
.pvr-wait-badge {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #fff3da;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 18px;
  font-size: 32px;
  color: #e0a92e;
}
.pvr-wait-title {
  font-size: 21px;
  font-weight: 700;
  color: $navy;
}
.pvr-wait-sub {
  font-size: 14px;
  color: $muted;
  margin-top: 8px;
  line-height: 1.55;
  max-width: 360px;
}
.pvr-pulse {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin-top: 18px;
  font-size: 13px;
  font-weight: 600;
  color: #b7791f;
  background: #fff7e6;
  padding: 7px 14px;
  border-radius: 20px;
}
.pvr-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #e0a92e;
}

/* ---- Sidebar ---- */
.pvr-side {
  width: 372px;
  flex-shrink: 0;
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 6px 28px rgba(0, 45, 94, 0.08);
  display: flex;
  flex-direction: column;
}
.pvr-appt {
  display: flex;
  gap: 13px;
  align-items: center;
  padding: 22px 22px 18px;
}
.pvr-avatar {
  width: 54px;
  height: 54px;
  border-radius: 50%;
  background: linear-gradient(135deg, #05e5ba, #00b48f);
  color: $navy;
  font-weight: 800;
  font-size: 19px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.pvr-kind {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: $teal;
  background: #e7fbf6;
  padding: 3px 8px;
  border-radius: 5px;
  display: inline-block;
}
.pvr-doctor {
  font-size: 18px;
  font-weight: 700;
  color: $navy;
  margin: 5px 0 2px;
}
.pvr-meta {
  font-size: 13px;
  color: $muted;
}

.pvr-block {
  padding: 18px 22px;
  border-top: 1px solid $line;
}
.pvr-block-head {
  display: flex;
  align-items: center;
  gap: 8px;

  h2 {
    font-size: 14px;
    font-weight: 700;
    color: $navy;
  }
  i {
    color: $navy;
    font-size: 16px;
  }
  .pvr-opt {
    font-size: 11px;
    color: $muted;
    font-weight: 500;
    margin-left: auto;
  }
}
.pvr-block-desc {
  font-size: 12.5px;
  color: $muted;
  line-height: 1.5;
  margin: 7px 0 12px;
}
.pvr-attach {
  width: 100%;
  border: 1.5px dashed #c5d2e0;
  background: #fafbfd;
  border-radius: 11px;
  padding: 14px;
  cursor: pointer;
  font-size: 13.5px;
  font-weight: 600;
  color: $navy;
  font-family: inherit;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}
.pvr-file-input {
  display: none;
}
.pvr-file {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #f4f7fb;
  border-radius: 10px;
  padding: 10px 12px;
  margin-top: 9px;
}
.pvr-file-ic {
  width: 32px;
  height: 32px;
  border-radius: 7px;
  background: #e7fbf6;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #05b48f;
}
.pvr-file-meta {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.pvr-file-name {
  font-size: 13px;
  font-weight: 600;
  color: $text;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.pvr-file-size {
  font-size: 11px;
  color: $muted;
}
.pvr-file-x {
  margin-left: auto;
  color: $muted;
  cursor: pointer;
  font-size: 15px;
}

.pvr-token {
  width: 100%;
  margin-top: 4px;
  padding: 13px 14px;
  border: 1.5px solid $line;
  border-radius: 11px;
  font-family: inherit;
  font-size: 15px;
  letter-spacing: 2px;
  color: $text;

  &::placeholder {
    letter-spacing: 0;
    color: #aab4c2;
  }
}

.pvr-cta-wrap {
  padding: 20px 22px;
  border-top: 1px solid $line;
  margin-top: auto;
}
.pvr-cta {
  width: 100%;
  border: none;
  border-radius: 12px;
  background: $navy;
  color: #fff;
  font-family: inherit;
  font-size: 15.5px;
  font-weight: 700;
  padding: 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
}
.pvr-cta--cancel {
  background: #fff;
  color: #c0392b;
  border: 1.5px solid $line;
}
.pvr-cta-hint {
  text-align: center;
  font-size: 12px;
  color: $muted;
  margin-top: 10px;
}

/* ---- Responsive: mobile apila ---- */
@media (max-width: 760px) {
  .pvr-layout {
    flex-direction: column;
  }
  .pvr-side {
    width: 100%;
  }
  .pvr-stage {
    min-height: 320px;
    padding: 32px 20px;
  }
}
</style>
